import { Center, SimpleGrid, Text } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { useGetDatasetVersionFiles } from '@/queries/datasetQueries'
import { FileMetadata, FileStatus } from '@/types/dataset'
import { MediaCard } from '../MediaCard/MediaCard'
import { MediaFileName } from '../MediaGallery/MediaFileName'
import { MediaGallery } from '../MediaGallery/MediaGallery'
import { MediaGalleryOverlay } from '../MediaGallery/MediaGalleryOverlay'

type DatasetVersionDetailsProps = {
  versionId: string
  onOverlayChange: (opened: boolean) => void
}

export const DatasetVersionDetails = ({
  versionId,
  onOverlayChange
}: DatasetVersionDetailsProps) => {
  const [activeFile, setActiveFile] = useState<FileMetadata | null>(null)

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetDatasetVersionFiles({
      versionId
    })

  const files = data?.pages?.flatMap((page) => page.results) || []

  const handleMediaClick = (file: FileMetadata) => {
    setActiveFile(file)
    onOverlayChange(true)
  }

  const handleGalleryClose = () => {
    setActiveFile(null)
    onOverlayChange(false)
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="datasets.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      {files.length === 0 && !isFetching && (
        <Center mih={120}>
          <Text>
            <FormattedMessage id="datasets.preview.noFiles" />
          </Text>
        </Center>
      )}

      <InfiniteScroll
        hasMore={hasNextPage}
        isFetching={isFetching}
        onLoadMore={() => void fetchNextPage()}
      >
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4, xl: 6 }} spacing="xl">
          {files.map((file) => (
            <MediaCard
              key={file.id}
              fileName={file.name}
              thumbnailUrl={file.thumbnail}
              isLabeled={file.status === FileStatus.Labeled}
              onClick={() => handleMediaClick(file)}
            />
          ))}
        </SimpleGrid>
      </InfiniteScroll>

      <MediaGalleryOverlay
        opened={!!activeFile}
        toolbar={
          activeFile && (
            <MediaFileName
              fileName={activeFile.name}
              fileType={activeFile.media_type}
            />
          )
        }
        onClose={handleGalleryClose}
      >
        <MediaGallery
          files={files}
          activeFile={activeFile}
          showThumbnails
          onSlideChange={setActiveFile}
        />
      </MediaGalleryOverlay>
    </>
  )
}
